import { useEffect, useMemo, useRef } from 'react'
import { addEdge, applyEdgeChanges, applyNodeChanges, Background, BackgroundVariant, Controls, MarkerType, ReactFlow, type Connection, type EdgeChange, type NodeChange, type ReactFlowInstance } from '@xyflow/react'
import ProcessFlowEdge, { type ProcessCanvasEdge } from '@/frontend/features/Fde/ProcessFlowEdge'
import ProcessFlowNode, { type ProcessCanvasNode } from '@/frontend/features/Fde/ProcessFlowNode'
import { processNodeDimensions } from '@/frontend/features/Fde/layoutProcessFlow'
import type { FdeCopy, ModelElement, ProcessCanvasNodeDefinition, ProcessDataCoverage, ProcessDataCoverageState, ProcessEdgeDefinition, ProcessTransitionKind } from '@/frontend/features/Fde/types'

interface Props {
  nodes: ProcessCanvasNodeDefinition[]
  edges: ProcessEdgeDefinition[]
  elements: ModelElement[]
  dataCoverage: ProcessDataCoverage
  selectedNodeId: string | null
  selectedEdgeId: string | null
  editable: boolean
  fitViewKey?: number
  copy: FdeCopy
  onNodesChange: (nodes: ProcessCanvasNodeDefinition[]) => void
  onEdgesChange: (edges: ProcessEdgeDefinition[]) => void
  onSelectNode: (nodeId: string | null) => void
  onSelectEdge: (edgeId: string | null) => void
}

const nodeTypes = { process: ProcessFlowNode }
const edgeTypes = { process: ProcessFlowEdge }
const edgeColors: Record<ProcessTransitionKind, string> = {
  sequence: 'var(--muted-foreground)', conditional: '#d97706', exception: '#e11d48',
  handoff: '#0891b2', return: '#7c3aed', retry: '#7c3aed', cancel: '#dc2626',
}

export default function ProcessFlowCanvas(props: Props) {
  const flowRef = useRef<ReactFlowInstance<ProcessCanvasNode, ProcessCanvasEdge> | null>(null)
  const elementNames = useMemo(() => new Map(props.elements.map((element) => [String(element.id), element.name])), [props.elements])
  const coverageByNode = useMemo(() => new Map(props.dataCoverage.nodes.map((node) => [node.node_id, node.requirements])), [props.dataCoverage.nodes])
  const flowNodes = useMemo<ProcessCanvasNode[]>(() => props.nodes.map((node) => ({
    id: node.id,
    type: 'process',
    position: node.position,
    ...processNodeDimensions(node),
    data: {
      definition: node,
      active: node.id === props.selectedNodeId,
      kindLabel: props.copy.workspace[`process_node_kind_${node.kind}`],
      connectionsLabel: props.copy.workspace.process_context_links_short,
      evidencesLabel: props.copy.workspace.process_evidences_short,
      context: [
        ...node.ontology_refs.map((reference) => elementNames.get(String(reference.element_id))),
      ].filter((value): value is string => Boolean(value)).slice(0, 2),
      dataCoverage: nodeCoverage(coverageByNode.get(node.id) || []),
      dataCoverageLabel: props.copy.workspace.process_data_short,
    },
  })), [coverageByNode, elementNames, props.copy.workspace, props.nodes, props.selectedNodeId])
  const flowEdges = useMemo<ProcessCanvasEdge[]>(() => props.edges.map((edge) => {
    const transitionKind = edge.transition_kind || 'sequence'
    const reversibility = edge.reversibility || 'unknown'
    const label = edge.label || edge.condition || (reversibility === 'irreversible' ? props.copy.workspace.process_irreversible : undefined)
    return {
    id: edge.id, source: edge.source, target: edge.target, label,
    type: 'process',
    data: { transitionKind },
    markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
    style: {
      strokeWidth: edge.id === props.selectedEdgeId ? 2.8 : 1.8,
      stroke: edge.id === props.selectedEdgeId ? 'var(--primary)' : edgeColors[transitionKind],
      strokeDasharray: ['exception', 'return', 'retry'].includes(transitionKind) ? '6 4' : undefined,
    },
  }}), [props.copy.workspace.process_irreversible, props.edges, props.selectedEdgeId])

  useEffect(() => {
    if (props.fitViewKey === undefined || !flowRef.current) return
    const frame = requestAnimationFrame(() => {
      void flowRef.current?.fitView({ padding: 0.22, maxZoom: 1, duration: 180 })
    })
    return () => cancelAnimationFrame(frame)
  }, [props.fitViewKey])

  function changeNodes(changes: NodeChange<ProcessCanvasNode>[]) {
    if (!changes.some((change) => ['position', 'remove', 'add', 'replace'].includes(change.type))) return
    const next = applyNodeChanges(changes, flowNodes)
    props.onNodesChange(next.map((node) => ({ ...node.data.definition, position: node.position })))
  }

  function changeEdges(changes: EdgeChange<ProcessCanvasEdge>[]) {
    if (!changes.some((change) => ['remove', 'add', 'replace'].includes(change.type))) return
    const next = applyEdgeChanges(changes, flowEdges)
    props.onEdgesChange(next.map((edge) => {
      const existing = props.edges.find((candidate) => candidate.id === edge.id)
      return existing || { id: edge.id, source: edge.source, target: edge.target, transition_kind: 'sequence', reversibility: 'unknown', evidence_ids: [] }
    }))
  }

  function connect(connection: Connection) {
    const next = addEdge<ProcessCanvasEdge>({ ...connection, id: crypto.randomUUID(), type: 'process', data: { transitionKind: 'sequence' } }, flowEdges)
    props.onEdgesChange(next.map((edge) => props.edges.find((candidate) => candidate.id === edge.id) || {
      id: edge.id, source: edge.source, target: edge.target, transition_kind: 'sequence', reversibility: 'unknown', evidence_ids: [],
    }))
  }

  return (
    <ReactFlow<ProcessCanvasNode, ProcessCanvasEdge>
      nodes={flowNodes}
      edges={flowEdges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      fitView
      fitViewOptions={{ padding: 0.22, maxZoom: 1 }}
      minZoom={0.25}
      maxZoom={1.6}
      nodesDraggable={props.editable}
      nodesConnectable={props.editable}
      edgesReconnectable={false}
      elementsSelectable
      deleteKeyCode={props.editable ? ['Backspace', 'Delete'] : null}
      proOptions={{ hideAttribution: true }}
      onInit={(instance) => { flowRef.current = instance }}
      onNodesChange={props.editable ? changeNodes : undefined}
      onEdgesChange={props.editable ? changeEdges : undefined}
      onConnect={props.editable ? connect : undefined}
      onNodeClick={(_, node) => { props.onSelectEdge(null); props.onSelectNode(node.id) }}
      onEdgeClick={(_, edge) => { props.onSelectNode(null); props.onSelectEdge(edge.id) }}
      onPaneClick={() => { props.onSelectNode(null); props.onSelectEdge(null) }}
      className="fde-process-flow"
    >
      <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="var(--border)" />
      <Controls showInteractive={false} position="bottom-left" />
    </ReactFlow>
  )
}

function nodeCoverage(requirements: ProcessDataCoverage['nodes'][number]['requirements']) {
  if (requirements.length === 0) return null
  const priority: ProcessDataCoverageState[] = ['missing', 'partial', 'proposed', 'confirmed']
  const state = priority.find((candidate) => requirements.some((requirement) => requirement.state === candidate)) || 'confirmed'
  return {
    state,
    mapped: requirements.filter((requirement) => requirement.state !== 'missing').length,
    total: requirements.length,
  }
}
